/**
 * Heat-map-canvas v2 — vervangt React Flow voor view-kinds met
 * `layout_mode = "heat-map"` (analytics-pack `analytics.heat-map`).
 *
 * Verschil met v1:
 *  - Config leeft in `Diagram.attributes` (target_type, vertical_attribute,
 *    horizontal_attribute, color_start, color_end, apply_color,
 *    dot_fill_color, dot_text_color) i.p.v. een stash-entry in viewState.
 *    AttributePanel toont 'm.
 *  - Ondersteunt zowel enum- als numerieke-attributen op beide assen.
 *  - Kleur: één start- en één eindkleur vormen een diagonale cel-gradient
 *    (links-onder = start, rechts-boven = eind), aan/uit via "Kleur
 *    toepassen". De bolletjes volgen hun eigen pickers (dot_fill_color /
 *    dot_text_color), standaard wit met donker nummer.
 *  - Numerieke as zonder enum-tegenhanger: ±10 buckets met "nice number"-
 *    grenzen (1/2/5/10/…). Enum-met-N-waarden + numerieke as: numerieke
 *    range wordt in N gelijke buckets verdeeld → vierkant N×N-grid.
 *  - Tekst-kleur in bolletje volgt luminance-check op de achtergrond,
 *    zodat lichte/donkere kleuren beide leesbaar zijn.
 *  - Klik op een bolletje selecteert het concept (AttributePanel volgt
 *    automatisch zodat z'n attributen rechts verschijnen).
 */

import type { AttributeSchema, Concept, Diagram } from "@ae/domain";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";

interface HeatMapCanvasV2Props {
  diagram: Diagram;
}

type AxisMode = "enum" | "numeric" | "unconfigured";

interface AxisModel {
  mode: AxisMode;
  attrName: string;
  /** Enum-options (in declaratie-volgorde) of bucket-labels voor numeriek. */
  buckets: { label: string; min?: number; max?: number; enumValue?: string }[];
  /** Voor enum-modus: rauwe options-array (voor same-enum-detectie). */
  enumOptions?: readonly string[];
}

interface PlottedConcept {
  concept: Concept;
  vBucket: number; // -1 = onbekend
  hBucket: number;
  vNumeric?: number | undefined;
  hNumeric?: number | undefined;
  /** 1-based volgnummer voor weergave als rondje in de matrix + legenda. */
  number: number;
}

export function HeatMapCanvasV2({ diagram }: HeatMapCanvasV2Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const revision = useWorkspaceData((s) => s.revision);
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);

  const attrs = diagram.attributes ?? {};
  const targetKey = typeof attrs.target_type === "string" ? attrs.target_type : "";
  const vertical = typeof attrs.vertical_attribute === "string" ? attrs.vertical_attribute : "";
  const horizontal =
    typeof attrs.horizontal_attribute === "string" ? attrs.horizontal_attribute : "";
  // Kleur-config: één start- en één eindkleur voor een diagonale
  // cel-gradient (links-onder = start, rechts-boven = eind), met een
  // "Kleur toepassen"-toggle. Default = aan.
  const applyColor = attrs.apply_color !== false;
  const colorStart = typeof attrs.color_start === "string" ? attrs.color_start : "#dc2626";
  const colorEnd = typeof attrs.color_end === "string" ? attrs.color_end : "#16a34a";
  // Richting van het kleurverloop: horizontaal (links→rechts), verticaal
  // (onder→boven) of beide (diagonaal). Default beide.
  const colorDirection: "horizontal" | "vertical" | "both" =
    attrs.color_direction === "horizontal" || attrs.color_direction === "vertical"
      ? attrs.color_direction
      : "both";
  // Bolletje-kleuren — eigen pickers, los van de cel-gradient. Default
  // wit (donker nummer) zodat ze leesbaar op de gradient staan.
  const dotFillOverride =
    typeof attrs.dot_fill_color === "string" ? attrs.dot_fill_color : undefined;
  const dotTextOverride =
    typeof attrs.dot_text_color === "string" ? attrs.dot_text_color : undefined;
  // Objecten-heatmap plot puur op type/entity-niveau. De per-instance
  // (twee-bolletjes-)variant is teruggedraaid: objecten krijgen geen
  // per-instance waarden (M7 doet de tijd-dimensie via ConceptState).

  const { concepts, schemas, typeLabel } = useMemo(() => {
    void revision;
    const reg = getRegistry();
    const store = useWorkspaceData.getState().store;
    const all = store.listConcepts();
    let schemaList: AttributeSchema[] = [];
    let label = "";
    let filterFn: (c: Concept) => boolean;
    if (targetKey.startsWith("stereotype:")) {
      const id = targetKey.slice("stereotype:".length);
      const st = reg.getStereotype(id);
      schemaList = [...(st?.attributes ?? [])];
      const applies = st ? reg.appliesToTypeIds(st) : [];
      const baseId = applies.find((b) => b !== "*");
      if (baseId) {
        const baseType = reg.getElementType(baseId);
        if (baseType?.attributes) schemaList.push(...baseType.attributes);
      }
      label = st ? resolveBilingual(st.label, lang) : id;
      filterFn = (c) => c.stereotypes.includes(id);
    } else if (targetKey.startsWith("type:")) {
      const id = targetKey.slice("type:".length);
      const et = reg.getElementType(id);
      schemaList = [...(et?.attributes ?? [])];
      label = et ? resolveBilingual(et.label, lang) : id;
      filterFn = (c) => c.typeId === id;
    } else {
      return { concepts: [] as Concept[], schemas: [] as AttributeSchema[], typeLabel: "" };
    }
    return {
      concepts: all.filter(filterFn),
      schemas: schemaList,
      typeLabel: label,
    };
  }, [revision, targetKey, lang]);

  const vOrder = readOrderAttr(attrs, vertical);
  const hOrder = readOrderAttr(attrs, horizontal);
  const vAxis = useMemo(
    () => buildAxis(vertical, schemas, concepts, lang, vOrder, "vertical"),
    [vertical, schemas, concepts, lang, vOrder],
  );
  const hAxis = useMemo(
    () => buildAxis(horizontal, schemas, concepts, lang, hOrder, "horizontal"),
    [horizontal, schemas, concepts, lang, hOrder],
  );

  const reorderAxis = (
    which: "vertical" | "horizontal",
    sourceValue: string,
    targetIndex: number,
  ) => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    const axis = which === "vertical" ? vAxis : hAxis;
    const attrName = which === "vertical" ? vertical : horizontal;
    if (axis.mode !== "enum") return;
    const current = axis.buckets.map((b) => b.enumValue ?? "").filter((v) => v !== "");
    const fromIdx = current.indexOf(sourceValue);
    if (fromIdx === -1) return;
    const next = [...current];
    next.splice(fromIdx, 1);
    const insertAt = targetIndex > fromIdx ? targetIndex - 1 : targetIndex;
    next.splice(Math.max(0, Math.min(next.length, insertAt)), 0, sourceValue);
    useWorkspaceData
      .getState()
      .setDiagramAttribute(workspaceId, branchId, diagram.id, `${attrName}_order`, next);
  };

  const plotted = useMemo(() => placeConcepts(concepts, vAxis, hAxis), [concepts, vAxis, hAxis]);

  const name = resolveBilingual(diagram.name, lang, "nl");
  // Alleen tonen wanneer er werkelijk inhoud is — anders zou
  // resolveBilingual op een leeg {}-object "(unnamed)" teruggeven.
  const description =
    diagram.description && (diagram.description.nl || diagram.description.en)
      ? resolveBilingual(diagram.description, lang)
      : "";

  const configured = vAxis.mode !== "unconfigured" && hAxis.mode !== "unconfigured";

  // Titel-font schaalt mee met de cel-grootte (zelfde helper als de grid-
  // labels), zodat titel + labels samen groter worden bij drukke cellen.
  const { fontScale: titleFontScale } = computeCellMetrics(plotted);

  // Resolve axis-title labels (bilingual schema-label of attr-name fallback).
  const vAxisTitle = useMemo(() => {
    const s = schemas.find((sc) => sc.name === vAxis.attrName);
    return s ? resolveBilingual(s.label, lang) : vAxis.attrName;
  }, [schemas, vAxis.attrName, lang]);
  const hAxisTitle = useMemo(() => {
    const s = schemas.find((sc) => sc.name === hAxis.attrName);
    return s ? resolveBilingual(s.label, lang) : hAxis.attrName;
  }, [schemas, hAxis.attrName, lang]);

  // Zoom-state — scale rond het matrix-midden. Het grid is via flexbox
  // gecentreerd in de viewport (zowel horizontaal als verticaal); met
  // `transformOrigin: center center` schaalt 't dan natuurlijk rond zijn
  // eigen middelpunt. Bij wheel-events veranderen we alleen de zoom-
  // factor — geen pan-berekening nodig.
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hovered = useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((z) => clamp(z * (e.deltaY < 0 ? 1.1 : 1 / 1.1), 0.3, 4));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  // Toetsenbord +/-/0 — consistent met de overige diagram-canvases (geen
  // modifier; editable-guard; alleen wanneer de muis boven de heat-map is).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!hovered.current || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = document.activeElement;
      if (
        t instanceof HTMLElement &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setZoom((z) => clamp(z * 1.1, 0.3, 4));
      } else if (e.key === "-") {
        e.preventDefault();
        setZoom((z) => clamp(z / 1.1, 0.3, 4));
      } else if (e.key === "0") {
        e.preventDefault();
        setZoom(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Toolbar-positie — gebruiker kan 'm slepen naar elk van de vier
  // hoeken. Default = rechts-onder. Persist in component-state, niet
  // in workspace-store (lokale UX-keuze).
  const [toolbarPos, setToolbarPos] = useState<
    "top-left" | "top-right" | "bottom-left" | "bottom-right"
  >("bottom-right");
  const toolbarPosClass: Record<typeof toolbarPos, string> = {
    "top-left": "top-3 left-3",
    "top-right": "top-3 right-3",
    "bottom-left": "bottom-3 left-3",
    "bottom-right": "bottom-3 right-3",
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel
    <div
      className="flex h-full w-full flex-col overflow-hidden bg-white"
      onDoubleClick={(e) => {
        // Dubbelklik op de canvas-achtergrond opent het AttributePanel
        // met de diagram-config. Klikken op een bolletje wordt door
        // ConceptChip afgevangen + stopPropagation → die selecteert
        // 't concept i.p.v. het diagram.
        if (e.defaultPrevented) return;
        const ui = useUi.getState();
        ui.selectDiagram(diagram.id);
        ui.setAttributePanelOpen(true);
      }}
    >
      <div className="relative flex flex-1 overflow-hidden">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel */}
        <div
          ref={scrollRef}
          onMouseEnter={() => {
            hovered.current = true;
          }}
          onMouseLeave={() => {
            hovered.current = false;
          }}
          className="flex flex-1 items-center justify-center overflow-hidden p-6"
        >
          {!configured ? (
            <div className="max-w-md rounded-md border border-slate-300 bg-slate-50 p-6 text-center text-slate-600 text-sm">
              <div className="mb-2 font-medium text-slate-800">
                {t("heatMap.v2.notConfiguredTitle")}
              </div>
              <div>{t("heatMap.v2.notConfiguredBody")}</div>
            </div>
          ) : (
            <div
              className="flex flex-col items-center gap-3"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: "center center",
              }}
            >
              {/* Diagram-titel: gecentreerd boven het grid, groter dan de
                  as-/cel-tekst, en meezoomend (binnen de scale-transform).
                  Dubbelklik opent het AttributePanel voor naam-bewerking. */}
              <div className="text-center">
                {/* biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel */}
                <div
                  className="inline-block cursor-pointer font-semibold text-slate-800 hover:text-blue-600"
                  style={{ fontSize: `${1.5 * titleFontScale}rem` }}
                  title={t("heatMap.editNameTitle")}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    const ui = useUi.getState();
                    ui.selectDiagram(diagram.id);
                    ui.setAttributePanelOpen(true);
                  }}
                >
                  {name}
                </div>
                {description && (
                  <div
                    className="mt-1 text-slate-500"
                    style={{ fontSize: `${0.875 * titleFontScale}rem` }}
                  >
                    {description}
                  </div>
                )}
              </div>
              <HeatMapGrid
                plotted={plotted}
                vAxis={vAxis}
                hAxis={hAxis}
                vAxisTitle={vAxisTitle}
                hAxisTitle={hAxisTitle}
                applyColor={applyColor}
                colorStart={colorStart}
                colorEnd={colorEnd}
                colorDirection={colorDirection}
                lang={lang}
                typeLabel={typeLabel}
                onReorder={reorderAxis}
                dotFillOverride={dotFillOverride}
                dotTextOverride={dotTextOverride}
              />
            </div>
          )}
        </div>
        {configured && (
          <div
            className={`absolute flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1 py-1 shadow-sm ${toolbarPosClass[toolbarPos]}`}
          >
            <button
              type="button"
              className="rounded px-1.5 py-0.5 text-slate-500 text-xs hover:bg-slate-100"
              title={t("heatMap.toolbar.move")}
              onClick={() => {
                const order: (typeof toolbarPos)[] = [
                  "top-left",
                  "top-right",
                  "bottom-right",
                  "bottom-left",
                ];
                setToolbarPos(
                  order[(order.indexOf(toolbarPos) + 1) % order.length] ?? "bottom-right",
                );
              }}
            >
              ⤵
            </button>
            <span className="h-4 w-px bg-slate-200" />
            <button
              type="button"
              className="rounded px-2 py-0.5 text-slate-600 text-sm hover:bg-slate-100"
              title={t("heatMap.toolbar.zoomOut")}
              onClick={() => setZoom((z) => clamp(z / 1.1, 0.3, 4))}
            >
              −
            </button>
            <button
              type="button"
              className="rounded px-2 py-0.5 text-slate-600 text-xs hover:bg-slate-100"
              title={t("heatMap.toolbar.resetZoom")}
              onClick={() => setZoom(1)}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              className="rounded px-2 py-0.5 text-slate-600 text-sm hover:bg-slate-100"
              title={t("heatMap.toolbar.zoomIn")}
              onClick={() => setZoom((z) => clamp(z * 1.1, 0.3, 4))}
            >
              +
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Bereken posities (relatief tot cel-midden) voor N bolletjes in één
 * matrixcel zonder overlap. Garantie: de hart-tot-hart-afstand tussen
 * elke twee bolletjes is minstens `spacing = dotSize + gap`.
 *
 *  - 1 bolletje → centraal
 *  - 2..6 → één ring; straal R = spacing / (2·sin(π/n)) zodat naburige
 *    bolletjes precies `spacing` uit elkaar staan
 *  - 7+ → één in 't midden, daaromheen concentrische ringen. Per ring is
 *    de capaciteit = omtrek / spacing (floor), zodat de hoek-dichtheid
 *    nooit tot overlap leidt. Ring-stralen lopen op met `spacing` zodat
 *    ook de radiale afstand klopt.
 *
 * Bounding-radius (`maxR` = afstand van midden tot verste dot-centrum)
 * wordt apart geretourneerd zodat de outer-laag de cel kan opschalen.
 */
export function dotPositions(
  n: number,
  dotSize: number,
  gap: number,
): { positions: Array<{ x: number; y: number }>; maxR: number } {
  if (n <= 1) return { positions: [{ x: 0, y: 0 }], maxR: 0 };
  const spacing = dotSize + gap; // minimale hart-tot-hart-afstand
  if (n <= 6) {
    const R = spacing / (2 * Math.sin(Math.PI / n));
    const positions: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < n; i++) {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      positions.push({ x: R * Math.cos(angle), y: R * Math.sin(angle) });
    }
    return { positions, maxR: R };
  }
  // 7+: midden + concentrische ringen met omtrek-gebaseerde capaciteit.
  const positions: Array<{ x: number; y: number }> = [{ x: 0, y: 0 }];
  let placed = 1;
  let ring = 1;
  let maxR = 0;
  while (placed < n) {
    const R = ring * spacing;
    const capacity = Math.max(1, Math.floor((2 * Math.PI * R) / spacing));
    const count = Math.min(capacity, n - placed);
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / count;
      positions.push({ x: R * Math.cos(angle), y: R * Math.sin(angle) });
    }
    maxR = R;
    placed += count;
    ring++;
  }
  return { positions, maxR };
}

function HeatMapGrid({
  plotted,
  vAxis,
  hAxis,
  vAxisTitle,
  hAxisTitle,
  applyColor,
  colorStart,
  colorEnd,
  colorDirection,
  lang,
  typeLabel,
  onReorder,
  dotFillOverride,
  dotTextOverride,
}: {
  plotted: PlottedConcept[];
  vAxis: AxisModel;
  hAxis: AxisModel;
  vAxisTitle: string;
  hAxisTitle: string;
  applyColor: boolean;
  colorStart: string;
  colorEnd: string;
  colorDirection: "horizontal" | "vertical" | "both";
  lang: string;
  typeLabel: string;
  onReorder: (which: "vertical" | "horizontal", sourceValue: string, targetIndex: number) => void;
  dotFillOverride?: string | undefined;
  dotTextOverride?: string | undefined;
}) {
  // Alleen kijken: de volgorde van de as-waarden is niet te verslepen.
  const readOnly = useSession((s) => s.readOnly);
  const { t } = useTranslation();
  const rows = vAxis.buckets.length;
  const cols = hAxis.buckets.length;
  if (rows === 0 || cols === 0) {
    return (
      <div className="m-auto max-w-md rounded-md border border-amber-300 bg-amber-50 p-6 text-center text-sm text-amber-900">
        {t("heatMap.v2.noAxisValues")}
      </div>
    );
  }

  const cellOf = (r: number, c: number) =>
    plotted.filter((p) => p.vBucket === r && p.hBucket === c);

  // Cel-gradient (wanneer "Kleur toepassen" aan). Richting via
  // color_direction: horizontaal (links→rechts), verticaal (onder→boven)
  // of beide (diagonaal links-onder→rechts-boven). Bij uit: neutraal.
  const cellBg = (r: number, c: number): string | undefined => {
    if (!applyColor) return undefined;
    let t: number;
    if (colorDirection === "horizontal") {
      t = cols <= 1 ? 0 : c / (cols - 1);
    } else if (colorDirection === "vertical") {
      t = rows <= 1 ? 0 : (rows - 1 - r) / (rows - 1);
    } else {
      const denom = Math.max(1, rows + cols - 2);
      t = (rows - 1 - r + c) / denom;
    }
    return lerpColor(colorStart, colorEnd, t);
  };

  // Border-kleur: bij gekleurde cellen wit zodat de gradient niet wordt
  // onderbroken; bij neutrale cellen slate-300 voor zichtbare grid-lijnen.
  const cellBorder = applyColor ? "#ffffff" : "#cbd5e1";

  // Cell-grootte + font-schaal via gedeelde helper. Cel groeit mee met de
  // drukst-bezette cel; labels + (in de parent gerenderde) titel schalen
  // met dezelfde fontScale mee.
  const { cellW, cellH, fontScale } = computeCellMetrics(plotted);
  const DOT_SIZE = DOT_SIZE_OBJ;
  const DOT_GAP = DOT_GAP_OBJ;
  const labelFont = `${0.75 * fontScale}rem`;

  return (
    <div className="m-auto">
      <div className="mb-2 text-slate-500 text-xs">
        {typeLabel} ({new Set(plotted.map((p) => p.concept.id)).size})
      </div>
      {/* Verticale as-titel staat links naast de waarde-labels, geroteerd
          90° counterclockwise zodat 'ie verticaal leest. Horizontale
          as-titel staat onder de bottom-headers. */}
      <div className="flex items-start gap-6">
        <div className="flex items-stretch">
          <div className="flex items-center pr-1">
            <div
              className="whitespace-nowrap font-semibold text-slate-600 uppercase tracking-wide"
              style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                fontSize: labelFont,
              }}
            >
              {vAxisTitle}
            </div>
          </div>
          <table className="border-collapse">
            <tbody>
              {/* buckets staan al in visual-top-to-bottom volgorde (buildAxis
                heeft pack-options omgekeerd voor de verticale as zodat de
                laatste enum-optie bovenaan komt — standaard grafiek-y-as-
                conventie). Direct itereren. */}
              {vAxis.buckets.map((v, rowIdx) => (
                <tr key={v.label}>
                  <th
                    className="cursor-grab pr-3 text-right align-middle font-medium text-slate-700 active:cursor-grabbing"
                    style={{ fontSize: labelFont }}
                    draggable={vAxis.mode === "enum" && !!v.enumValue && !readOnly}
                    onDragStart={(e) => {
                      if (!v.enumValue) return;
                      e.dataTransfer.setData("text/heatmap-axis-v", v.enumValue);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      if (e.dataTransfer.types.includes("text/heatmap-axis-v")) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      }
                    }}
                    onDrop={(e) => {
                      const src = e.dataTransfer.getData("text/heatmap-axis-v");
                      if (!src) return;
                      e.preventDefault();
                      onReorder("vertical", src, rowIdx);
                    }}
                    title={vAxis.mode === "enum" ? t("heatMap.dragReorderValues") : undefined}
                  >
                    {v.label}
                  </th>
                  {hAxis.buckets.map((h, colIdx) => {
                    const items = cellOf(rowIdx, colIdx);
                    const bg = cellBg(rowIdx, colIdx);
                    const { positions } = dotPositions(items.length, DOT_SIZE, DOT_GAP);
                    return (
                      <td
                        key={h.label}
                        className="p-0 align-middle"
                        style={{
                          backgroundColor: bg ?? (applyColor ? "#f8fafc" : "transparent"),
                          border: `1px solid ${cellBorder}`,
                          width: cellW,
                          height: cellH,
                        }}
                      >
                        <div className="relative" style={{ width: cellW, height: cellH }}>
                          {items.map((p, dotIdx) => {
                            const pos = positions[dotIdx] ?? { x: 0, y: 0 };
                            return (
                              <div
                                key={p.concept.id}
                                style={{
                                  position: "absolute",
                                  left: cellW / 2 + pos.x - DOT_SIZE / 2,
                                  top: cellH / 2 + pos.y - DOT_SIZE / 2,
                                }}
                              >
                                <ConceptDot
                                  plotted={p}
                                  lang={lang}
                                  size={DOT_SIZE}
                                  fillOverride={dotFillOverride}
                                  textOverride={dotTextOverride}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th />
                {hAxis.buckets.map((h, colIdx) => (
                  <th
                    key={h.label}
                    className="cursor-grab px-3 py-2 text-center font-medium text-slate-700 active:cursor-grabbing"
                    style={{ minWidth: 120, fontSize: labelFont }}
                    draggable={hAxis.mode === "enum" && !!h.enumValue && !readOnly}
                    onDragStart={(e) => {
                      if (!h.enumValue) return;
                      e.dataTransfer.setData("text/heatmap-axis-h", h.enumValue);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      if (e.dataTransfer.types.includes("text/heatmap-axis-h")) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      }
                    }}
                    onDrop={(e) => {
                      const src = e.dataTransfer.getData("text/heatmap-axis-h");
                      if (!src) return;
                      e.preventDefault();
                      onReorder("horizontal", src, colIdx);
                    }}
                    title={hAxis.mode === "enum" ? t("heatMap.dragReorderValues") : undefined}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
              <tr>
                <th />
                <th
                  colSpan={hAxis.buckets.length}
                  className="pt-1 text-center font-semibold text-slate-600 uppercase tracking-wide"
                  style={{ fontSize: labelFont }}
                >
                  {hAxisTitle}
                </th>
              </tr>
            </tfoot>
          </table>
        </div>
        <ConceptLegend
          plotted={plotted}
          lang={lang}
          dotFillOverride={dotFillOverride}
          dotTextOverride={dotTextOverride}
          matrixHeight={cellH * rows}
        />
      </div>
    </div>
  );
}

function ConceptLegend({
  plotted,
  lang,
  dotFillOverride,
  dotTextOverride,
  matrixHeight,
}: {
  plotted: PlottedConcept[];
  lang: string;
  dotFillOverride?: string | undefined;
  dotTextOverride?: string | undefined;
  matrixHeight: number;
}) {
  // Eén legenda-regel per concept (één bolletje, op type/entity-niveau).
  const groups = useMemo(() => {
    const byId = new Map<string, { dot: PlottedConcept; number: number; concept: Concept }>();
    for (const p of plotted) {
      if (byId.has(p.concept.id)) continue;
      byId.set(p.concept.id, { dot: p, number: p.number, concept: p.concept });
    }
    return [...byId.values()].sort((a, b) => a.number - b.number);
  }, [plotted]);
  if (groups.length === 0) return null;
  const ROW_HEIGHT = 32;
  const rowsPerCol = Math.max(1, Math.floor(matrixHeight / ROW_HEIGHT));
  const columns: (typeof groups)[] = [];
  for (let i = 0; i < groups.length; i += rowsPerCol) {
    columns.push(groups.slice(i, i + rowsPerCol));
  }
  return (
    <div className="flex shrink-0 gap-4">
      {columns.map((col, ci) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positionele grid-cel; het grid wordt volledig herberekend zonder reordering, dus de index is een stabiele identiteit
        <table key={ci} className="border-collapse">
          <tbody>
            {col.map((g) => {
              const dot = g.dot;
              return (
                <tr key={g.concept.id} className="hover:bg-slate-50">
                  <td className="pr-2 align-middle">
                    <div className="flex items-center gap-1">
                      <ConceptDot
                        plotted={dot}
                        lang={lang}
                        size={22}
                        fillOverride={dotFillOverride}
                        textOverride={dotTextOverride}
                      />
                    </div>
                  </td>
                  <td
                    className="min-w-[140px] max-w-[240px] cursor-pointer py-1 align-middle text-slate-700 text-xs leading-tight"
                    onClick={() => {
                      const ui = useUi.getState();
                      ui.selectConcept(dot.concept.id);
                      ui.setAttributePanelOpen(true);
                    }}
                  >
                    {resolveBilingual(dot.concept.name, lang, "nl")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ))}
    </div>
  );
}

/**
 * Weergave-kleur voor een bolletje. De cellen dragen de gradient; het
 * bolletje volgt z'n eigen picker (dot_fill_color) of is standaard wit
 * met donker nummer zodat 't leesbaar op de gekleurde cel staat.
 */
function dotColorFor(fillOverride: string | undefined): { bg: string; border: string } {
  if (fillOverride) return { bg: fillOverride, border: darken(fillOverride, 0.2) };
  return { bg: "#ffffff", border: "#475569" };
}

function ConceptDot({
  plotted,
  lang,
  size = 26,
  fillOverride,
  textOverride,
}: {
  plotted: PlottedConcept;
  lang: string;
  size?: number;
  fillOverride?: string | undefined;
  textOverride?: string | undefined;
}) {
  const { concept } = plotted;
  const name = resolveBilingual(concept.name, lang, "nl");
  const { bg, border } = dotColorFor(fillOverride);
  const textColor = textOverride ?? contrastText(bg);
  return (
    <button
      type="button"
      title={name}
      onClick={(e) => {
        e.stopPropagation();
        const ui = useUi.getState();
        ui.selectConcept(concept.id);
        ui.setAttributePanelOpen(true);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
      }}
      className="inline-flex items-center justify-center rounded-full border-2 font-semibold transition hover:brightness-95"
      style={{
        backgroundColor: bg,
        borderColor: border,
        color: textColor,
        width: size,
        height: size,
        fontSize: size <= 22 ? 10 : 11,
        lineHeight: 1,
      }}
    >
      {plotted.number}
    </button>
  );
}

// =====================================================================
// Axis-modellen
// =====================================================================

/**
 * Resolved display-label voor een enum-optie. Volgorde:
 *  1. Pack-declared `option_labels[value]` (bilingual) — voor packs die
 *     expliciet menselijke labels meeleveren (bv. `read-write` → "lezen
 *     en schrijven").
 *  2. Fallback: vervang `_` door spaties zodat `proof_of_concept` op de
 *     as als "proof of concept" verschijnt. Raw waarde blijft de
 *     canonical opslag-vorm; alleen presentatie wordt opgeschoond.
 */
function displayEnumLabel(value: string, schema: AttributeSchema, lang: string): string {
  const explicit = schema.option_labels?.[value];
  if (explicit) return resolveBilingual(explicit, lang);
  return value.replace(/_/g, " ");
}

function buildAxis(
  attrName: string,
  schemas: AttributeSchema[],
  concepts: Concept[],
  lang: string,
  customOrder?: readonly string[] | undefined,
  axis: "vertical" | "horizontal" = "horizontal",
): AxisModel {
  if (!attrName) return { mode: "unconfigured", attrName: "", buckets: [] };
  const schema = schemas.find((s) => s.name === attrName);
  if (!schema) return { mode: "unconfigured", attrName, buckets: [] };
  const baseType = schema.type.endsWith("?") ? schema.type.slice(0, -1) : schema.type;
  if (baseType === "enum") {
    const packOpts = schema.options ?? [];
    // Voor de verticale as: pack-volgorde wordt OMGEKEERD als default
    // visual-top-to-bottom (laatste enum-optie bovenaan, eerste onderaan
    // — standaard grafiek-y-as-conventie). Voor horizontaal: pack-orde
    // ongewijzigd (eerste links, laatste rechts). customOrder (drag-
    // reorder door gebruiker) verwijst nu naar de visuele top-to-bottom
    // volgorde van enum-waarden, dus wordt zonder verdere transformatie
    // toegepast.
    const defaultOrder = axis === "vertical" ? [...packOpts].reverse() : packOpts;
    let opts = defaultOrder;
    if (customOrder && customOrder.length > 0) {
      const packSet = new Set(packOpts);
      const ordered = customOrder.filter((v) => packSet.has(v));
      const remaining = defaultOrder.filter((o) => !ordered.includes(o));
      opts = [...ordered, ...remaining];
    }
    return {
      mode: "enum",
      attrName,
      enumOptions: opts,
      buckets: opts.map((o) => ({ label: displayEnumLabel(o, schema, lang), enumValue: o })),
    };
  }
  if (baseType === "int") {
    const values: number[] = [];
    for (const c of concepts) {
      const v = c.attributes?.[attrName];
      if (typeof v === "number" && Number.isFinite(v)) values.push(v);
    }
    if (values.length === 0) {
      return { mode: "unconfigured", attrName, buckets: [] };
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    return {
      mode: "numeric",
      attrName,
      buckets: numericBuckets(min, max),
    };
  }
  return { mode: "unconfigured", attrName, buckets: [] };
}

/**
 * Lees de custom axis-volgorde uit `Diagram.attributes[<attr>_order]`.
 * Returnt undefined als 'r geen array of geen geldige strings staan,
 * waarna `buildAxis` terugvalt op de pack-declaratie-volgorde.
 */
function readOrderAttr(
  attrs: Record<string, unknown>,
  attrName: string,
): readonly string[] | undefined {
  if (!attrName) return undefined;
  const raw = attrs[`${attrName}_order`];
  if (!Array.isArray(raw)) return undefined;
  const arr = raw.filter((v): v is string => typeof v === "string");
  return arr.length > 0 ? arr : undefined;
}

// Celafmeting + font-schaal. Single source of truth zodat zowel de
// grid-labels als de (in de parent gerenderde) titel identiek meeschalen
// wanneer cellen groter worden door meer bolletjes.
const DOT_SIZE_OBJ = 26;
const DOT_GAP_OBJ = 4;
const CELL_PADDING_OBJ = 6;
const MIN_W_OBJ = 120;
const MIN_H_OBJ = 90;
function computeCellMetrics(plotted: PlottedConcept[]): {
  cellW: number;
  cellH: number;
  fontScale: number;
} {
  let maxPerCell = 0;
  const counts = new Map<string, number>();
  for (const p of plotted) {
    if (p.vBucket < 0 || p.hBucket < 0) continue;
    const k = `${p.vBucket},${p.hBucket}`;
    const n = (counts.get(k) ?? 0) + 1;
    counts.set(k, n);
    if (n > maxPerCell) maxPerCell = n;
  }
  const { maxR } = dotPositions(maxPerCell, DOT_SIZE_OBJ, DOT_GAP_OBJ);
  const requiredDim = maxR * 2 + DOT_SIZE_OBJ + CELL_PADDING_OBJ * 2;
  const cellW = Math.max(MIN_W_OBJ, requiredDim);
  const cellH = Math.max(MIN_H_OBJ, requiredDim);
  // Font groeit mee met de celhoogte t.o.v. de minimumcel, gedempt + gecapt.
  const fontScale = Math.min(2.5, Math.max(1, 1 + (cellH / MIN_H_OBJ - 1) * 0.7));
  return { cellW, cellH, fontScale };
}

function placeConcepts(concepts: Concept[], vAxis: AxisModel, hAxis: AxisModel): PlottedConcept[] {
  const dots: PlottedConcept[] = [];
  concepts.forEach((c, idx) => {
    const number = idx + 1;
    // Eén bolletje per concept op entity-waarden van het concept.
    const vEntity = c.attributes?.[vAxis.attrName];
    const hEntity = c.attributes?.[hAxis.attrName];
    dots.push({
      concept: c,
      vBucket: bucketOf(vAxis, vEntity),
      hBucket: bucketOf(hAxis, hEntity),
      vNumeric: typeof vEntity === "number" ? vEntity : undefined,
      hNumeric: typeof hEntity === "number" ? hEntity : undefined,
      number,
    });
  });
  return dots;
}

function bucketOf(axis: AxisModel, value: unknown): number {
  if (axis.mode === "enum") {
    if (typeof value !== "string") return -1;
    return axis.buckets.findIndex((b) => b.enumValue === value);
  }
  if (axis.mode === "numeric") {
    if (typeof value !== "number" || !Number.isFinite(value)) return -1;
    return axis.buckets.findIndex(
      (b) =>
        value >= (b.min ?? Number.NEGATIVE_INFINITY) && value < (b.max ?? Number.POSITIVE_INFINITY),
    );
  }
  return -1;
}

/**
 * Bouwt ±10 "nice number"-buckets voor een numerieke as. Bucket-grootte
 * wordt afgerond op de 1/2/5-reeks (× 10^n), zodat as-grenswaarden
 * leesbaar zijn (10, 20, 50, 100 i.p.v. willekeurige decimalen).
 */
function numericBuckets(min: number, max: number): { label: string; min: number; max: number }[] {
  if (min === max) {
    return [{ label: String(min), min: min, max: min + 1 }];
  }
  const rawStep = (max - min) / 10;
  const step = niceStep(rawStep);
  const lower = Math.floor(min / step) * step;
  const upper = Math.ceil(max / step) * step;
  const buckets: { label: string; min: number; max: number }[] = [];
  for (let v = lower; v < upper; v += step) {
    const next = v + step;
    buckets.push({ label: `${formatNum(v)}–${formatNum(next)}`, min: v, max: next });
  }
  return buckets;
}

function niceStep(rawStep: number): number {
  if (rawStep <= 0) return 1;
  const exponent = Math.floor(Math.log10(rawStep));
  const fraction = rawStep / 10 ** exponent;
  let niceFraction: number;
  if (fraction < 1.5) niceFraction = 1;
  else if (fraction < 3.5) niceFraction = 2;
  else if (fraction < 7.5) niceFraction = 5;
  else niceFraction = 10;
  return niceFraction * 10 ** exponent;
}

function formatNum(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

// =====================================================================
// Kleurhulpfuncties
// =====================================================================

function lerpColor(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return a;
  const r = Math.round(ca.r + (cb.r - ca.r) * t);
  const g = Math.round(ca.g + (cb.g - ca.g) * t);
  const blue = Math.round(ca.b + (cb.b - ca.b) * t);
  return `rgb(${r}, ${g}, ${blue})`;
}

function parseHex(s: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(s);
  if (!m) {
    // Fallback voor rgb()
    const rgb = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(s);
    if (rgb) return { r: +rgb[1]!, g: +rgb[2]!, b: +rgb[3]! };
    return null;
  }
  const hex = m[1]!;
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
}

function darken(color: string, amount: number): string {
  const c = parseHex(color);
  if (!c) return color;
  return `rgb(${Math.round(c.r * (1 - amount))}, ${Math.round(c.g * (1 - amount))}, ${Math.round(c.b * (1 - amount))})`;
}

/** WCAG-style contrast pick — wit op donker, donker op licht. */
function contrastText(bg: string): string {
  const c = parseHex(bg);
  if (!c) return "#111827";
  // sRGB luminance benadering: 0.2126R + 0.7152G + 0.0722B (0..255)
  const lum = (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
  return lum > 0.6 ? "#111827" : "#ffffff";
}
